"use client";

import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { IoPricetagOutline, IoTimeOutline, IoClose } from "react-icons/io5";
import { REST_API_URL } from "../api/config";
import { buildHeaders } from "../api/buildHeaders";
import { getCurrencySymbol } from "../utils/currency/getCurrencySymbol";
import EditClientModalCalendar from "./EditClientModalCalendar";
import { PrimaryButton } from "../components/buttons/primaryButton/PrimaryButton.tsx";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const fetchUserInfo = async (token) => {
  try {
    const headers = buildHeaders();

    const response = await fetch(`${BASE_URL}/auth/userinfo/`, { headers });

    const raw = await response.text();

    if (!response.ok) return { currency: "USD" };

    const userData = JSON.parse(raw);

    localStorage.setItem("userInfo", JSON.stringify(userData));
    localStorage.setItem("userInfoTimestamp", String(Date.now()));

    return userData;
  } catch (error) {
    console.error("Error fetching user info:", error);
    return { currency: "USD" };
  }
};

export default function CalendarDayModal({
  open,
  onClose,
  appointments,
  date,
  userStatus,
  onSaveSuccess,
  isGoogleEventDay,
}) {
  const { t } = useTranslation();
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [userCurrency, setUserCurrency] = useState("USD");
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    const getUserInfo = async () => {
      if (!open) return;

      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (token) {
        const userInfo = await fetchUserInfo(token);
        setUserCurrency(userInfo?.currency || "USD");

        const browserTimeZone = getUserTimeZone();
        setUserTimeZone(browserTimeZone);
      }
    };

    getUserInfo();
  }, [open]);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setTimeout(() => {
        setIsVisible(true);
        document.body.style.overflow = "hidden";
      }, 10);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        document.body.style.overflow = "unset";
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const filteredAppointments = useMemo(() => {
    if (selectedWorkers.length === 0) return appointments;

    return appointments.filter((a) => {
      const master =
        a.master || (userStatus === "individual" ? "You" : "Master");
      return selectedWorkers.includes(master);
    });
  }, [appointments, selectedWorkers, userStatus]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDisplayPrice = (price) => {
    if (!price && price !== 0) return "0.00";

    try {
      const priceNum = typeof price === "string" ? parseFloat(price) : price;
      if (isNaN(priceNum)) return "0.00";

      return priceNum.toFixed(2);
    } catch (error) {
      console.error("Error formatting price:", error);
      return "0.00";
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return "";
    const normalized = status.trim().toLowerCase();
    switch (normalized) {
      case "upcoming":
        return t("home.statusUpcoming");
      case "completed":
        return t("home.statusCompleted");
      case "canceled":
        return t("home.statusCanceled");
      case "doing":
        return t("home.statusDoing");
      case "late":
        return t("home.statusLate");
      default:
        return status;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const today = date || new Date();
      const dateString = today.toISOString().split("T")[0];
      const fullDateTime = `${dateString}T${timeString}`;
      const dateObj = new Date(fullDateTime);

      return dateObj.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: userTimeZone,
      });
    } catch (error) {
      console.error("Error formatting time:", error);
      return timeString;
    }
  };

  const formatDateWithTimeZone = (dateObj) => {
    if (!dateObj) return "";
    try {
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: userTimeZone,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
      onClick={handleOverlayClick}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className={`relative w-full max-w-5xl h-[85vh] mx-4 
    bg-white 
    rounded-[20px] 
    overflow-hidden 
    shadow-2xl 
    flex flex-col 
    transition-all duration-300 transform
    ${isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b bg-white">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-normal text-gray-900">
                {date
                  ? t("home.calendarModalAppointmentsForDate", {
                      date: formatDateWithTimeZone(date),
                    })
                  : t("home.calendarModalAppointmentsForSelectedDate")}
              </h2>
              {isGoogleEventDay && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    background: "#dcfce7",
                    color: "#16a34a",
                    borderRadius: 999,
                    padding: "2px 10px",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  <span style={{ fontSize: 14 }}>📅</span>
                  Google Calendar
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t("home.calendarModalAppointmentsCount", {
                count: filteredAppointments.length,
              })}
            </p>
          </div>
          <PrimaryButton
            label=""
            leftIcon={<IoClose className="w-6 h-6 text-gray-600" />}
            onClick={onClose}
            variant="ghost"
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredAppointments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAppointments.map((a, index) => {
                const currencyCode = a.currency || userCurrency || "USD";
                const currencySymbol = getCurrencySymbol(currencyCode);
                const displayPrice = formatDisplayPrice(a.price);

                return (
                  <div
                    key={a.id}
                    className="group bg-white rounded-xl border p-5 hover:shadow-lg transition-all duration-300 hover:border-blue-300 cursor-pointer"
                    onClick={() => {
                      setSelectedEvent(a);
                      setIsEditModalOpen(true);
                    }}
                    style={{
                      animationDelay: `${index * 50}ms`,
                      animation: "slideIn 0.3s ease-out forwards",
                      opacity: 0,
                      transform: "translateY(10px)",
                    }}
                  >
                    <div className="mb-4">
                      <div className="flex items-start gap-3 mb-3">
                        <img
                          src={a.avatar}
                          alt="client"
                          className="w-12 h-12 rounded-full border-2 border-white shadow"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {a.name}
                          </h3>
                          <p className="text-sm text-blue-600 truncate">
                            {a.service}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {t("home.calendarModalStatusLabel")}{" "}
                            <span
                              className={`font-medium ${
                                a.status?.toLowerCase() === "upcoming"
                                  ? "text-green-600"
                                  : a.status?.toLowerCase() === "completed"
                                    ? "text-blue-600"
                                    : "text-gray-600"
                              }`}
                            >
                              {getStatusLabel(a.status)}
                            </span>
                          </p>
                        </div>
                      </div>
                      {a.comment && a.comment !== "No comment provided" && (
                        <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg mt-2 h-auto">
                          {a.comment}
                        </p>
                      )}
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-3 text-sm hover:translate-x-1 transition-transform">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                          <IoPricetagOutline className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-gray-700 font-medium">
                            {currencySymbol}
                            {displayPrice}
                          </div>
                          <div className="text-xs text-gray-500">
                            {t("home.calendarModalTotalAmountLabel")}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-sm hover:translate-x-1 transition-transform">
                        <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                          <IoTimeOutline className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-gray-700 font-medium">
                            {formatTime(a.timeStart)} – {formatTime(a.timeEnd)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {t("home.calendarModalDurationLabel")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              {isGoogleEventDay && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: "#f0fdf4",
                    border: "1px solid #86efac",
                    borderRadius: 12,
                    padding: "12px 20px",
                    marginBottom: 24,
                    color: "#16a34a",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  <span style={{ fontSize: 20 }}>📅</span>
                  <span>This day has Google Calendar events</span>
                </div>
              )}
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-6">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t("home.calendarModalNoAppointmentsFound")}
              </h3>
              <p className="text-gray-600 max-w-md mb-6">
                {selectedWorkers.length > 0
                  ? t("home.calendarModalNoAppointmentsFilteredDesc")
                  : t("home.calendarModalNoAppointmentsDateDesc")}
              </p>
              {selectedWorkers.length > 0 && (
                <button
                  onClick={() => setSelectedWorkers([])}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-200 shadow hover:shadow-md"
                >
                  {t("home.calendarModalClearFilters")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {t("home.calendarModalShowingCount", {
                filtered: filteredAppointments.length,
                total: appointments.length,
              })}
              {selectedWorkers.length > 0 && (
                <span className="ml-2">
                  {t("home.calendarModalFilteredByWorkers", {
                    count: selectedWorkers.length,
                  })}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {selectedWorkers.length > 0 && (
                <button
                  onClick={() => setSelectedWorkers([])}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {t("home.calendarModalResetFilters")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`}</style>

      {isEditModalOpen && selectedEvent && (
        <div onClick={(e) => e.stopPropagation()}>
          <EditClientModalCalendar
            isOpen={isEditModalOpen}
            event={selectedEvent}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedEvent(null);
            }}
            onSaveSuccess={() => {
              setIsEditModalOpen(false);
              setSelectedEvent(null);
              if (typeof onSaveSuccess === "function") {
                onSaveSuccess();
              }
            }}
            userCurrency={userCurrency}
          />
        </div>
      )}
    </div>
  );
}
